import React from 'react';
import PropTypes from 'prop-types';
import { TextField as MUITextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { colors } from '@constants/colors';
import { fonts } from '@constants/fonts';

// tss-react only supports one function for the whole styles object (theme, params), not a
// function per-rule like @mui/styles allowed — these were inlined into that single function
// below (using params.multiline/params.floatingLabelFixed) instead of being called per-rule.
const useStyles = makeStyles()((theme, { floatingLabelFixed = false, multiline = false }) => ({
  inputRoot: {
    fontSize: fonts.md,
    '&.Mui-disabled': {
      color: colors.neutral.gray,
      WebkitTextFillColor: colors.neutral.gray
    }
  },
  // v5 shrank the input element's own padding/height (4px 0 5px / 1.4375em) from v4's
  // (6px 0 7px / 1.1876em) — restored here to keep every field's height/spacing unchanged.
  // For multiline (textarea) fields, MUI keeps the input itself at padding:0/height:auto in
  // both v4 and v5 and puts the real padding on the root's 'multiline' modifier class instead
  // (below) — so this override must not apply here, or the two paddings stack (double height).
  // lineHeight is set explicitly (not just inherited from root) because root's own font-size
  // is forced to 14px by inputRoot below, while the input's real font-size is 16px (set via
  // the theme's MuiInputBase.input override in root.js) — v5's own line-height is in `em`,
  // so left alone it resolves against root's 14px, not the input's real 16px, landing ~1px
  // too tall. Explicit here so it resolves against the input's own, correct font-size.
  inputInput: multiline
    ? { padding: 0, height: 'auto', lineHeight: '1.1876em' }
    : { padding: '6px 0 7px', height: '1.1876em', lineHeight: '1.1876em' },
  // v5 shrank this root-level multiline padding too (4px 0 5px vs v4's 6px 0 7px).
  inputMultilineRoot: {
    padding: '6px 0 7px'
  },
  inputUnderline: {
    '&:before': {
      borderBottom: `1px solid ${colors.neutral.silver}`
    }
  },
  inputLabel: floatingLabelFixed
    ? { position: 'unset', color: colors.neutral.gray }
    : {
      color: colors.neutral.gray,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      bottom: '0px',
      maxWidth: '100%'
    },
  inputLabelShrunk: floatingLabelFixed
    ? { transform: 'unset', fontSize: fonts.sm }
    : {
      fontSize: fonts.sm,
      overflow: 'unset',
      textOverflow: 'unset',
      whiteSpace: 'unset'
    },
  inputLabelFocused: {
    color: `${colors.neutral.gray} !important`
  },
  inputLabelError: {
    color: `${theme.palette.error.main} !important`
  },
  formControl: {
    'label + &': {
      marginTop: '0'
    }
  }
}));

export const TextField = React.forwardRef((props, ref) => {
  const {
    floatingLabelFixed = false,
    fullWidth = true,
    autoComplete = 'off',
    disableUnderline = false,
    variant = 'standard',
    InputProps,
    InputLabelProps,
    FormHelperTextProps,
    inputProps,
    slotProps,
    ...data
  } = props;

  const { classes } = useStyles({ floatingLabelFixed, multiline: props.multiline });

  return (
    <MUITextField
      {...data}
      ref={ref}
      variant={variant}
      fullWidth={fullWidth}
      label={props.label}
      autoComplete={autoComplete}
      // v9 removed TextField's own InputProps/InputLabelProps/FormHelperTextProps/inputProps
      // convenience props entirely — only slotProps works now. Translated here so consumers
      // don't need to change (still accepted via propTypes below for backward compatibility).
      slotProps={{
        ...slotProps,
        inputLabel: {
          ...InputLabelProps,
          ...(floatingLabelFixed && {
            classes: {
              root: classes.inputLabel,
              focused: classes.inputLabelFocused,
              error: classes.inputLabelError,
              shrink: classes.inputLabelShrunk,
              ...InputLabelProps?.classes
            },
            shrink: true
          }),
          ...slotProps?.inputLabel
        },
        input: {
          ...InputProps,
          ...(variant === 'standard' && { disableUnderline }),
          classes: {
            root: classes.inputRoot,
            input: classes.inputInput,
            multiline: classes.inputMultilineRoot,
            ...(variant === 'standard' && {
              underline: classes.inputUnderline,
              ...(floatingLabelFixed && { formControl: classes.formControl })
            }),
            ...InputProps?.classes
          },
          ...slotProps?.input
        },
        htmlInput: {
          ...inputProps,
          ...slotProps?.htmlInput
        },
        ...(FormHelperTextProps && {
          formHelperText: { ...FormHelperTextProps, ...slotProps?.formHelperText }
        })
      }}
    />
  );
});

TextField.propTypes = {
  FormHelperTextProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  InputProps: PropTypes.object,
  autoComplete: PropTypes.string,
  disableUnderline: PropTypes.bool,
  floatingLabelFixed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  inputProps: PropTypes.object,
  label: PropTypes.string,
  multiline: PropTypes.bool,
  slotProps: PropTypes.object,
  style: PropTypes.object,
  variant: PropTypes.string
};

export default TextField;
